import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const getCertName = (url) => {
  if (!url) return 'Certificate';
  try {
    const decoded = decodeURIComponent(url.split('?')[0]);
    const rawName = decoded.split('/').pop() || 'Certificate';
    return rawName.replace(/^\d+_/, '') || rawName;
  } catch {
    return 'Certificate';
  }
};

const ZoomBtn = ({ onClick, title, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#e2e8f0' : 'var(--branding-app-surface, #f1f5f9)',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        padding: '6px 12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        color: 'var(--cc-text-primary)',
        lineHeight: 1,
        transition: 'background 0.12s ease',
      }}
    >
      {children}
    </button>
  );
};

export default function CertificateViewerModal({ open, onClose, certificateUrl, title }) {
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setPdfNumPages(0);
        setPdfZoom(1);
        setCurrentPage(1);
      }, 150);
      return () => clearTimeout(t);
    }
  }, [open, certificateUrl]);

  const pageWidth = useMemo(() => Math.floor(600 * pdfZoom), [pdfZoom]);

  const handleZoomIn = useCallback(() => setPdfZoom(p => Math.min(3, +(p + 0.25).toFixed(2))), []);
  const handleZoomOut = useCallback(() => setPdfZoom(p => Math.max(0.5, +(p - 0.25).toFixed(2))), []);
  const handleZoomReset = useCallback(() => setPdfZoom(1), []);

  const handleScroll = useCallback((e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setCurrentPage(pdfNumPages);
      return;
    }
    const pageHeight = pageWidth * 1.4;
    const calculated = Math.floor(scrollTop / pageHeight) + 1;
    setCurrentPage(Math.max(1, Math.min(calculated, pdfNumPages)));
  }, [pdfNumPages, pageWidth]);

  if (!open || !certificateUrl) return null;

  const decodedUrl = (() => { try { return decodeURIComponent(certificateUrl); } catch { return certificateUrl; } })();
  const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/i.test(decodedUrl);
  const isPDF = /\.pdf(\?|$)/i.test(decodedUrl);
  const certName = title || getCertName(certificateUrl);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        padding: '20px',
      }}
    >
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: '16px',
        width: '90vw',
        height: '90vh',
        maxWidth: '1200px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
      }}>
        {/* Accent bar */}
        <div style={{
          height: '3px',
          background: 'var(--branding-app-primary, #3b82f6)',
          flexShrink: 0,
        }} />

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid #f1f5f9',
          flexShrink: 0,
          gap: '12px',
          minHeight: '60px',
        }}>
          {/* File info */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--cc-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {certName}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--branding-app-surface-panel-hint)',
              marginTop: '2px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              {isPDF ? 'PDF Document' : isImage ? 'Image' : 'Document'}
            </div>
          </div>

          {/* Zoom controls — PDF only */}
          {isPDF && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
              <ZoomBtn onClick={handleZoomOut} title="Zoom out">−</ZoomBtn>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--cc-text-primary)',
                minWidth: '46px',
                textAlign: 'center',
                background: 'var(--branding-app-surface-panel)',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '6px 8px',
              }}>
                {Math.round(pdfZoom * 100)}%
              </div>
              <ZoomBtn onClick={handleZoomIn} title="Zoom in">+</ZoomBtn>
              <ZoomBtn onClick={handleZoomReset} title="Reset zoom">Reset</ZoomBtn>
            </div>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              background: 'var(--branding-app-primary, #3b82f6)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flex: '0 0 auto',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
            Close
          </button>
        </div>

        {/* Content */}
        <div
          ref={scrollRef}
          onScroll={isPDF ? handleScroll : undefined}
          style={{
            flex: 1,
            overflow: 'auto',
            background: 'var(--branding-app-surface-panel)',
            display: 'flex',
            justifyContent: 'center',
            padding: '24px 20px',
          }}
        >
          {isImage ? (
            <img
              src={certificateUrl}
              alt={certName}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                objectFit: 'contain',
                display: 'block',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              }}
            />
          ) : isPDF ? (
            <Document
              file={certificateUrl}
              onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
              onLoadError={(err) => console.error('PDF load error:', err)}
              loading={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: 'var(--cc-text-muted)', fontSize: '14px' }}>
                  Loading PDF…
                </div>
              }
              error={
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', color: '#dc2626', gap: '12px' }}>
                  <span style={{ fontSize: '14px' }}>Failed to load PDF</span>
                  <a href={certificateUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--branding-app-primary)', fontSize: '13px' }}>
                    Open in new tab
                  </a>
                </div>
              }
            >
              {Array.from({ length: pdfNumPages }, (_, idx) => (
                <div key={`page_${idx + 1}`} style={{ marginBottom: '20px' }}>
                  <Page
                    pageNumber={idx + 1}
                    width={pageWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    renderMode="canvas"
                    loading={
                      <div style={{
                        width: pageWidth,
                        height: Math.floor(pageWidth * 1.41),
                        background: 'var(--cc-card-bg)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--branding-app-surface-panel-hint)',
                        fontSize: '13px',
                      }}>
                        Loading page {idx + 1}…
                      </div>
                    }
                  />
                </div>
              ))}
            </Document>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: 'var(--cc-text-muted)' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <p style={{ margin: 0, fontSize: '14px' }}>Unsupported file type</p>
              <a href={certificateUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--branding-app-primary)', fontSize: '13px', textDecoration: 'underline' }}>
                Open in new tab
              </a>
            </div>
          )}
        </div>

        {/* Footer: page counter */}
        {isPDF && pdfNumPages > 0 && (
          <div style={{
            padding: '10px 20px',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'var(--branding-app-surface)',
              borderRadius: '20px',
              padding: '5px 14px',
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--cc-text-secondary)',
            }}>
              Page{' '}
              <span style={{ color: 'var(--branding-app-primary)', fontWeight: '700' }}>{currentPage}</span>
              {' '}of{' '}
              <span>{pdfNumPages}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
